/**
 * The Learn tab — every room's, since there is one library (master plan §1).
 *
 * PHASE 9: it renders from the lesson source. Phase 2b's `LearnPanel` was a transcribed
 * list of titles with "not written yet" under it; this is the same two shelves, drawn
 * from `lessons.json` (built from `content/lessons/`), with every written page a link to
 * its public `/learn/<id>` and every page the outline still promises listed as being
 * written — the player is told what is coming, not "coming soon".
 *
 * YOUR SYLLABUS is the Study reordered by the player's own leak report. It is Pro, like
 * the report's segments it is sorted by; a free account sees the outline's order and the
 * lock, and a signed-out visitor the same. Reading is free and public (§8).
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { api, type LeakReportResponse } from "../lib/api";
import { useMeContext } from "../lib/MeContext";
import { shelf, syllabus, STUDY_TRACKS, TRACK_LABEL, type Entry, type SyllabusEntry } from "./library";
import "./learn.css";

type Report = { state: "none" } | { state: "loading" } | { state: "ok"; data: LeakReportResponse } | { state: "err" };

export function LearnTab() {
  const { isSignedIn, getToken } = useAuth();
  const { me } = useMeContext();
  const pro = me?.plan.id === "pro";
  const [report, setReport] = useState<Report>({ state: "none" });
  const [mode, setMode] = useState<"syllabus" | "outline">("syllabus");
  const tokenRef = useRef(getToken);
  tokenRef.current = getToken;

  /* The report is fetched only for Pro: for anyone else it is gated, and the syllabus
     is locked whatever it says. */
  useEffect(() => {
    if (!isSignedIn || !pro) { setReport({ state: "none" }); return; }
    let on = true;
    setReport({ state: "loading" });
    api.leaks(tokenRef.current)
      .then((r) => { if (on) setReport({ state: "ok", data: r }); })
      .catch(() => { if (on) setReport({ state: "err" }); });
    return () => { on = false; };
  }, [isSignedIn, pro]);

  const primer = shelf("primer");
  const study = shelf("study");
  const segments = report.state === "ok" && !report.data.gated ? report.data.segments : [];
  const ordered = syllabus(study, segments);
  const moved = ordered.filter((s) => s.because).length;
  const showSyllabus = pro && mode === "syllabus" && moved > 0;

  return (
    <div className="tab-learn learn">
      <section className="panel learn-shelf" aria-labelledby="learn-primer">
        <p className="learn-kicker">Shelf one · the Marshal</p>
        <h2 id="learn-primer">The Primer</h2>
        <p className="muted">
          Twelve pages, the rules and the screen, free and public. Every page ends in
          something to try.
        </p>
        <ol className="learn-entries">{primer.map((e, i) => <EntryRow key={i} e={e} n={i + 1} />)}</ol>
      </section>

      <section className="panel learn-shelf" aria-labelledby="learn-study">
        <p className="learn-kicker">Shelf two · the Card Sharp</p>
        <h2 id="learn-study">The Study</h2>
        <p className="muted">
          Twenty-five lessons in seven tracks, each paired with the drill that practises
          it. Every range on these pages is drawn from the chart the trainer deals from.
        </p>

        <Syllabus pro={pro} signedIn={!!isSignedIn} report={report} moved={moved}
                  mode={mode} onMode={setMode} />

        {showSyllabus ? (
          <ol className="learn-entries learn-syllabus">
            {ordered.map((s, i) => <EntryRow key={i} e={s.entry} n={i + 1} because={s.because} />)}
          </ol>
        ) : (
          STUDY_TRACKS.map((t) => {
            const rows = study.filter((e) => (e.lesson ?? e.planned)!.track === t);
            return rows.length ? (
              <div key={t} className="learn-track">
                <h3>{TRACK_LABEL[t]}</h3>
                <ol className="learn-entries">{rows.map((e, i) => <EntryRow key={i} e={e} n={i + 1} />)}</ol>
              </div>
            ) : null;
          })
        )}
      </section>

      <p className="tab-note muted small">
        Pages are written in batches — the Primer first, then the Study a track at a time,
        Tournament first. A title without a link is on its way.
      </p>
    </div>
  );
}

/** The syllabus control: the Pro toggle, the "not enough yet" line, or the lock. */
function Syllabus({ pro, signedIn, report, moved, mode, onMode }: {
  pro: boolean; signedIn: boolean; report: Report; moved: number;
  mode: "syllabus" | "outline"; onMode: (m: "syllabus" | "outline") => void;
}) {
  if (!pro) {
    return (
      <aside className="learn-syllabus-lock" data-td="syllabus-lock">
        <p>
          <em className="learn-pro">Poker Pro</em>{" "}
          <b>Your Syllabus</b>. It puts these lessons in the order your own leak
          report says you need them, worst leak first, and moves them again as you drill.
          Below is the outline's order.
        </p>
        <Link className="btn-small" to={signedIn ? "/pricing" : "/sign-up"}>
          {signedIn ? "See Poker Pro" : "Create an account"}
        </Link>
      </aside>
    );
  }
  if (report.state === "loading") return <p className="muted small learn-syllabus-note">Reading your leak report…</p>;
  if (report.state === "err") return <p className="muted small learn-syllabus-note">Your leak report could not be read just now, so this is the outline's order.</p>;
  if (moved === 0) {
    return (
      <p className="muted small learn-syllabus-note">
        <b>Your Syllabus</b> reorders these by your leak report once a category has enough
        graded spots to mean something. Drill, and this list will sort itself.
      </p>
    );
  }
  return (
    <div className="learn-modes" role="group" aria-label="Order">
      <button type="button" className={mode === "syllabus" ? "on" : ""} onClick={() => onMode("syllabus")}>Your Syllabus</button>
      <button type="button" className={mode === "outline" ? "on" : ""} onClick={() => onMode("outline")}>The outline</button>
    </div>
  );
}

function EntryRow({ e, n, because }: { e: Entry; n: number; because?: SyllabusEntry["because"] }) {
  if (!e.lesson) {
    return (
      <li className="learn-entry learn-planned">
        <span className="learn-n num">{n}</span>
        <span className="learn-body"><b>{e.planned.title}</b><small>Being written</small></span>
      </li>
    );
  }
  const l = e.lesson;
  return (
    <li className="learn-entry">
      <span className="learn-n num">{n}</span>
      <Link className="learn-body" to={`/learn/${l.id}`}>
        <b>{l.title}</b>
        <small>{l.promise}</small>
        <span className="learn-meta">
          {l.readingMinutes} min
          {!l.free && <em className="learn-pro">Poker Pro</em>}
          {l.sample && <em className="learn-sample">Sample</em>}
          {because && (
            <em className="learn-because" title="From your leak report">
              Your leak: {because.label} · {because.evLostPer100!.toFixed(1)} per 100
            </em>
          )}
        </span>
      </Link>
    </li>
  );
}
